import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  type AiPreferences,
  AiPreferencesSchema,
  type AiSideEffects,
  type AiWorkspaceDefaults,
  type AiWorkspaceDefaultsPatch,
  type TimesheetAssistant,
} from '@timelyapp/integration-cloud-api-spec';
import { coerceAiPreferences } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/util.coerceAiPreferences';
import { debounce } from 'lodash-es';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import type { IState } from 'redux/state';
import debug, { LogLevel } from 'utils/debug';
import {
  safelyDelayRefreshAiPredictionsInDateRange,
  safelyDelayRetrainAiModel,
} from '~domains/ai/predictions/redux/predictions.actions';
import {
  getTicAiPreferences,
  getTicAiWorkspaceDefaultsData,
} from '~domains/ai/predictions/redux/predictions.selectors';
import { mergeWorkspaceDefaultsPatch } from '~domains/ai/preferences/utils/mergeWorkspaceDefaultsPatch';
import { getIsViewingTimesheet } from '~domains/ai/timesheet_proposals/redux/tic.timesheetProposals.slice';
import { type SaveAiPreferencesApiArg, ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { showToast } from '~redux/modules/toasts.actions';
import { getUserId } from '~redux/selectors/user';
import { trackNotableAiPreferencesChange } from '../analytics/trackNotableAiPreferencesChange';

const SAVE_AI_PREFERENCES_DEBOUNCE_SECONDS = 3;

/**
 * Helper thunk to enable AI with default (Zod) preferences.
 * @see saveAiPreferences for the actual API call.
 */
export const initializeAiPreferences = createAsyncThunk<
  void,
  void,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/initializeAiPreferences', async (_, thunkAPI): Promise<void> => {
  const defaults = AiPreferencesSchema.parse({ enabled: true });
  await thunkAPI.dispatch(saveAiPreferences({ preferences: defaults, sideEffect: 'initialize' }));
});

const runSaveAiPreferencesRequest = async (
  dispatch: ThunkDispatch<IState, any, Action>,
  getState: () => IState,
  payload: SaveAiPreferencesApiArg & { sideEffects: AiSideEffects }
): Promise<{ userToastShown: boolean }> => {
  const { sideEffects, ...rest } = payload;

  // Make API call first
  debug.template.ai('Executing saveAiPreferences', { payload });
  await dispatch(ticApi.endpoints.saveAiPreferences.initiate(rest)).unwrap();

  // Handle side-effects of the coercion
  const title = 'DEBUG AI PREFERENCES ';
  if (sideEffects.aiModelMustBeRetrained) {
    const message = 'AI preferences saved, must re-train AI model';
    dispatch(showToast({ type: 'info', title, message, logLevel: LogLevel.Debug }));
    dispatch(safelyDelayRetrainAiModel({ delayStrategy: 'debounce' }));
    return { userToastShown: false };
  } else if (sideEffects.aiSuggestionsMustBeRefreshed) {
    const isViewingTimesheet = getIsViewingTimesheet(getState());
    // Skip the timeline suggestions toast when on the AutoSheet view — it's not relevant there
    if (!isViewingTimesheet) {
      const message = 'Updating suggestions based on new preferences';
      dispatch(showToast({ type: 'info', message }));
    }
    dispatch(safelyDelayRefreshAiPredictionsInDateRange({ delayStrategy: 'debounce' }));
    return { userToastShown: !isViewingTimesheet };
  } else {
    const message = 'AI preferences saved, but no side-effects were fired.';
    dispatch(showToast({ type: 'warning', title, message, logLevel: LogLevel.Verbose }));
    return { userToastShown: false };
  }
};

/**
 * saveAiPreferences via debounce (max every 3 seconds)
 * @see coerceAiPreferences to understand how `sideEffects` are generated
 */
const _internal_debounce_saveAiPreferences = debounce(
  async (
    {
      dispatch,
      getState,
    }: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    payload: SaveAiPreferencesApiArg & { sideEffects: AiSideEffects }
  ) => {
    await runSaveAiPreferencesRequest(dispatch, getState, payload);
  },
  SAVE_AI_PREFERENCES_DEBOUNCE_SECONDS * 1_000
);

/**
 * Client-side update of AI (pre-coerced) preferences, with analytics tracking.
 * @warning You should have a great reason to call this directly.
 * @see saveAiPreferences for the thunk you likely want.
 */
export const updateAiPreferences = createAsyncThunk<
  void,
  Partial<AiPreferences>,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/updateAiPreferences', async (changedPrefs, { getState, dispatch }) => {
  const previous = getTicAiPreferences(getState() as IState);
  // We only get Partial's when id/updated_at get set for the first time
  const { preferences } = coerceAiPreferences({ ...previous, ...changedPrefs }, previous);

  dispatch(ticApi.util.updateQueryData('getAiPreferences', undefined, () => preferences));
});

/**
 * Merges the existing AI preferences with the new ones,
 * coerces preferences to stable state /w side-effects,
 * optimistically updates and sends to server (every 3s)
 *
 * @see updateAiPreferences for local update.
 * @see _internal_debounce_saveAiPreferences for debounced API call.
 */
export const saveAiPreferences = createAsyncThunk<
  { userToastShown: boolean } | undefined,
  { preferences: Partial<AiPreferences>; sideEffect?: 'initialize'; waitForServer?: boolean },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>(
  'tic/saveAiPreferences',
  async ({ preferences: _partialUpdate, sideEffect, waitForServer }, thunkAPI) => {
    const state = thunkAPI.getState();
    const timelyAccountId = getCurrentAccountId(state);

    const previousPrefs = getTicAiPreferences(state);
    const updatedPrefs = { ...previousPrefs, ..._partialUpdate };
    const { preferences, sideEffects } = coerceAiPreferences(updatedPrefs, previousPrefs);

    // Analytics tracking of notable changes
    trackNotableAiPreferencesChange({ previous: previousPrefs, current: preferences });

    // Optimistically update the local cache
    thunkAPI.dispatch(updateAiPreferences(preferences));

    const savePayload = {
      timelyAccountId,
      body: preferences,
      sideEffect, // <- API call side-effect
      sideEffects, // <- side-effects of the coercion
    };

    if (waitForServer) {
      return await runSaveAiPreferencesRequest(thunkAPI.dispatch, thunkAPI.getState, savePayload);
    }

    // Debounced API call & side-effects
    _internal_debounce_saveAiPreferences(thunkAPI, savePayload);
  }
);

/**
 * Saves workspace-level timesheet assistant preferences.
 * Uses the new workspace-scoped API endpoint.
 *
 * Accumulates rapid partial updates at module level so the debounced send
 * carries all intermediate changes (only changed fields — avoids sending
 * unchanged locked fields that would trigger a 403).
 */
const SAVE_WORKSPACE_PREFS_DEBOUNCE_SECONDS = 3;
let _pendingWorkspacePrefs: Partial<TimesheetAssistant> = {};
let _pendingWorkspacePrefsPatch: { undo: () => void } | null = null;

const _internal_debounce_saveWorkspacePreferences = debounce(
  async (dispatch: ThunkDispatch<IState, any, Action>, timelyAccountId: number) => {
    const body = _pendingWorkspacePrefs;
    const patchToUndo = _pendingWorkspacePrefsPatch;
    _pendingWorkspacePrefs = {};
    _pendingWorkspacePrefsPatch = null;
    debug.template.ai('Executing saveAiWorkspacePreferences', { body });
    try {
      await dispatch(
        ticApi.endpoints.saveAiWorkspacePreferences.initiate({ timelyAccountId, body })
      ).unwrap();
    } catch (error) {
      patchToUndo?.undo();
      throw error;
    }
  },
  SAVE_WORKSPACE_PREFS_DEBOUNCE_SECONDS * 1_000
);

export const saveTimesheetAssistantPreferences = createAsyncThunk<
  void,
  { preferences: Partial<TimesheetAssistant>; waitForServer?: boolean },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/saveTimesheetAssistantPreferences', async ({ preferences, waitForServer }, thunkAPI) => {
  const state = thunkAPI.getState();
  const timelyAccountId = getCurrentAccountId(state);

  // Optimistic update on workspace preferences cache (store patch for rollback)
  const patchResult = thunkAPI.dispatch(
    ticApi.util.updateQueryData('getAiWorkspacePreferences', { timelyAccountId }, draft => {
      Object.assign(draft.timesheet_assistant, preferences);
    })
  );

  if (waitForServer) {
    try {
      await thunkAPI
        .dispatch(
          ticApi.endpoints.saveAiWorkspacePreferences.initiate({
            timelyAccountId,
            body: preferences,
          })
        )
        .unwrap();
    } catch (error) {
      patchResult.undo();
      throw error;
    }
    return;
  }

  // Accumulate partials and patches so the last debounced call can roll back on failure
  _pendingWorkspacePrefs = { ..._pendingWorkspacePrefs, ...preferences };
  _pendingWorkspacePrefsPatch = patchResult;
  _internal_debounce_saveWorkspacePreferences(thunkAPI.dispatch, timelyAccountId);
});

/**
 * Patches the admin workspace defaults (enforced settings and note
 * instructions). Owners and admins only.
 *
 * The body is a PATCH the server merges over the saved document: absent keys
 * are kept, the rule maps merge per rule id (`null` deletes a rule, a `null`
 * map clears it) and `locked_fields` replaces the saved list only when
 * present. The optimistic update applies the same merge to the cached
 * defaults, and copies every locked field's value into the resolved
 * preferences. On success the cache takes the server's returned document; on
 * failure both patches are undone and the error rethrown.
 */
export const saveWorkspaceDefaults = createAsyncThunk<
  AiWorkspaceDefaults,
  AiWorkspaceDefaultsPatch,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/saveWorkspaceDefaults', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const timelyAccountId = getCurrentAccountId(state);
  const merged = mergeWorkspaceDefaultsPatch(getTicAiWorkspaceDefaultsData(state), payload);

  // Optimistic updates — store patches for rollback on failure
  const patchDefaults = thunkAPI.dispatch(
    ticApi.util.updateQueryData('getAiWorkspaceDefaults', { timelyAccountId }, draft => {
      draft.timesheet_assistant = merged.timesheet_assistant;
      if (payload.locked_fields !== undefined) {
        draft.locked_fields = payload.locked_fields;
      }
    })
  );

  const patchPreferences = thunkAPI.dispatch(
    ticApi.util.updateQueryData('getAiWorkspacePreferences', { timelyAccountId }, draft => {
      if (payload.locked_fields !== undefined) {
        draft.locked_fields = payload.locked_fields;
      }
      // Members resolve locked fields from the defaults; mirror that here.
      const resolved: Record<string, unknown> = draft.timesheet_assistant;
      const defaults: Record<string, unknown> = merged.timesheet_assistant;
      for (const field of draft.locked_fields ?? []) {
        if (defaults[field] !== undefined) resolved[field] = defaults[field];
      }
    })
  );

  try {
    const saved = await thunkAPI
      .dispatch(
        ticApi.endpoints.saveAiWorkspaceDefaults.initiate({
          timelyAccountId,
          body: payload,
        })
      )
      .unwrap();
    // The server owns document integrity; its answer replaces the optimistic merge.
    thunkAPI.dispatch(
      ticApi.util.upsertQueryData('getAiWorkspaceDefaults', { timelyAccountId }, saved)
    );
    return saved as AiWorkspaceDefaults;
  } catch (error) {
    patchDefaults.undo();
    patchPreferences.undo();
    throw error;
  }
});

/**
 * Resets the AI model for the current user.
 * (Thunk injects the necessary params, but otherwise straight-up RTKQ call)
 */
export const resetAiModel = createAsyncThunk<
  void,
  void,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/resetAiModel', async (_args, thunkAPI) => {
  const state = thunkAPI.getState();
  const timely_account_id = getCurrentAccountId(state);
  const timely_user_id = getUserId(state);

  await thunkAPI.dispatch(
    ticApi.endpoints.resetAiModel.initiate({
      body: { timely_account_id, timely_user_id },
    })
  );
});

/**
 * Internal only; Resets & deletes everything AI-related for the current user.
 */
export const resetEverythingAi = createAsyncThunk<
  void,
  void,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/resetEverythingAi', async (_args, thunkAPI) => {
  const state = thunkAPI.getState();
  const body = { timely_account_id: getCurrentAccountId(state) };
  await thunkAPI.dispatch(ticApi.endpoints.resetEverythingAi.initiate({ body }));
});
